/**
 * ItemJiraSyncPanel
 *
 * Allows a user to attach a priorities.ai item to a Jira epic or story.
 * Writes the mapping to entity_external_ids so the sync pipeline can track it.
 *
 * Also shows the live Jira status for each linked issue and lets the user
 * "close the loop" — archiving the item when Jira marks it done, either
 * automatically (via webhook) or manually via this panel.
 *
 * Displayed in the Classification tab of ItemDetailPage when Jira is connected.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ExternalLink,
  Link2,
  Link2Off,
  Loader2,
  Search,
  X,
  ChevronDown,
  AlertCircle,
  Check,
  RefreshCw,
  CheckCircle2,
  Clock,
  Circle,
} from 'lucide-react';
import { getIntegrationsApi, type JiraProject } from '../../../lib/integrationsApi';
import { registerExternalId, getExternalIds } from '../../../lib/connectors';
import { describeJiraSync, syncLinkedJiraIssue } from '../../../lib/jiraItemSync';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getCurrentWorkspaceId, getSafeSupabaseClient } from '../../../lib/supabaseSync';

// ── Types ─────────────────────────────────────────────────────────────────────

interface JiraIssue {
  id: string;
  key: string;
  summary: string;
  issueType: string;
  status: string;
  url: string;
}

interface JiraIssueStatus {
  issueKey: string;
  statusName: string;
  statusCategory: string; // 'new' | 'indeterminate' | 'done'
  isDone: boolean;
  resolution: string | null;
}

interface ExistingLink {
  connectorType: string;
  externalId: string;
  externalUrl: string | null;
  lastSyncedAt: string | null;
  mappingId?: string;
}

// ── Jira icon ─────────────────────────────────────────────────────────────────

const JiraIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.005 1.005 0 0 0 23.013 0z" />
  </svg>
);

// ── IssueTypeBadge ────────────────────────────────────────────────────────────

function IssueTypeBadge({ type }: { type: string }) {
  const isEpic = type.toLowerCase() === 'epic';
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${
        isEpic
          ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
          : 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
      }`}
    >
      {type}
    </span>
  );
}

// ── StatusBadge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: JiraIssueStatus }) {
  if (status.isDone) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/30 dark:text-[var(--text-ter)] border border-[var(--green)] dark:border-[var(--green)]">
        <CheckCircle2 className="h-3 w-3" />
        {status.statusName}
      </span>
    );
  }
  if (status.statusCategory === 'indeterminate') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/30 dark:text-[var(--mustard)] border border-[var(--mustard)] dark:border-[var(--mustard)]">
        <Clock className="h-3 w-3" />
        {status.statusName}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-[var(--surface)] text-[var(--text-sec)] border border-[var(--rule)]">
      <Circle className="h-3 w-3" />
      {status.statusName}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface ItemJiraSyncPanelProps {
  itemId: string;
  itemTitle?: string;
  /** Current exit_status of the item — used to suppress Close Loop CTA if already archived */
  exitStatus?: string;
  /** Called after the item is successfully archived via Close Loop */
  onLoopClosed?: () => void;
}

export function ItemJiraSyncPanel({ itemId, itemTitle, exitStatus, onLoopClosed }: ItemJiraSyncPanelProps) {
  const workspaceId = getCurrentWorkspaceId();

  // ── Existing links ───────────────────────────────────────────────────────
  const [links, setLinks] = useState<ExistingLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(true);

  // ── Live Jira status per linked issue ────────────────────────────────────
  const [statusMap, setStatusMap] = useState<Record<string, JiraIssueStatus>>({});
  const [statusLoading, setStatusLoading] = useState(false);

  // ── Close the loop ───────────────────────────────────────────────────────
  const [closingLoop, setClosingLoop] = useState(false);
  const [loopClosed, setLoopClosed] = useState(exitStatus === 'archived');
  const [confirmingClose, setConfirmingClose] = useState(false);

  // ── Link panel state ─────────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);

  // ── Project selection ────────────────────────────────────────────────────
  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProjectKey, setSelectedProjectKey] = useState('');

  // ── Issue search ─────────────────────────────────────────────────────────
  const [issueTypes, setIssueTypes] = useState<string[]>(['Epic', 'Story']);
  const [searchQuery, setSearchQuery] = useState('');
  const [issues, setIssues] = useState<JiraIssue[]>([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<JiraIssue | null>(null);

  // ── Save state ───────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jiraConnected, setJiraConnected] = useState<boolean | null>(null);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load existing links for this item
  const loadLinks = useCallback(async () => {
    if (!workspaceId) return;
    setLinksLoading(true);
    try {
      const allLinks = await getExternalIds(workspaceId, 'item', itemId);
      const jiraLinks = allLinks.filter((l) => l.connectorType === 'jira');

      const supabase = getSafeSupabaseClient();
      if (supabase && jiraLinks.length > 0) {
        const { data } = await supabase
          .from('entity_external_ids')
          .select('id, external_id')
          .eq('workspace_id', workspaceId)
          .eq('entity_type', 'item')
          .eq('internal_id', itemId)
          .eq('connector_type', 'jira')
          .eq('is_active', true);

        const idMap: Record<string, string> = {};
        for (const row of data ?? []) {
          idMap[row.external_id as string] = row.id as string;
        }
        setLinks(
          jiraLinks.map((l) => ({
            ...l,
            mappingId: idMap[l.externalId],
          }))
        );
      } else {
        setLinks(jiraLinks);
      }
    } finally {
      setLinksLoading(false);
    }
  }, [workspaceId, itemId]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  // Fetch live Jira status for all linked issues
  const refreshStatuses = useCallback(async (currentLinks: ExistingLink[]) => {
    if (!workspaceId || currentLinks.length === 0) return;
    setStatusLoading(true);
    try {
      const api = getIntegrationsApi(workspaceId);
      const results = await Promise.allSettled(
        currentLinks.map((l) => api.getJiraIssueStatus(l.externalId))
      );
      const newMap: Record<string, JiraIssueStatus> = {};
      for (const result of results) {
        if (result.status === 'fulfilled') {
          newMap[result.value.issueKey] = result.value;
        }
      }
      setStatusMap(newMap);
    } catch {
      // Status fetch is best-effort; silently skip on error
    } finally {
      setStatusLoading(false);
    }
  }, [workspaceId]);

  // Auto-fetch statuses once links are loaded (and Jira appears connected)
  useEffect(() => {
    if (!linksLoading && links.length > 0) {
      refreshStatuses(links);
    }
  }, [linksLoading, links, refreshStatuses]);

  // ── Close the loop ───────────────────────────────────────────────────────

  const handleCloseLoop = async () => {
    if (!workspaceId) return;
    setClosingLoop(true);
    setError(null);
    try {
      const supabase = getSafeSupabaseClient();
      if (!supabase) throw new Error('Database unavailable');

      // Pull the first linked issue key for metadata (most items link to one)
      const primaryLink = links[0];
      const primaryStatus = primaryLink ? statusMap[primaryLink.externalId] : undefined;

      const { data: item } = await supabase
        .from('items')
        .select('metadata')
        .eq('id', itemId)
        .eq('workspace_id', workspaceId)
        .single();

      const existingMetadata = (item?.metadata as Record<string, unknown>) ?? {};

      const { error: updateError } = await supabase
        .from('items')
        .update({
          exit_status: 'archived',
          metadata: {
            ...existingMetadata,
            jira_closed: {
              issueKey: primaryLink?.externalId ?? null,
              closedAt: new Date().toISOString(),
              jiraStatus: primaryStatus?.statusName ?? null,
              resolution: primaryStatus?.resolution ?? null,
              closedBy: 'manual',
            },
          },
          updated_at: new Date().toISOString(),
        })
        .eq('id', itemId)
        .eq('workspace_id', workspaceId);

      if (updateError) throw new Error(updateError.message);

      setLoopClosed(true);
      setConfirmingClose(false);
      onLoopClosed?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close the loop');
    } finally {
      setClosingLoop(false);
    }
  };

  // Derived: are any linked issues done in Jira?
  const anyDoneInJira = links.some((l) => statusMap[l.externalId]?.isDone);

  // Load Jira projects when panel opens
  useEffect(() => {
    if (!isOpen || !workspaceId) return;
    setProjectsLoading(true);
    getIntegrationsApi(workspaceId)
      .listJiraProjects()
      .then((p) => {
        setProjects(p);
        setJiraConnected(true);
        if (p.length > 0 && !selectedProjectKey) {
          setSelectedProjectKey(p[0].key);
        }
      })
      .catch(() => {
        setJiraConnected(false);
      })
      .finally(() => setProjectsLoading(false));
  }, [isOpen, workspaceId, selectedProjectKey]);

  // Search issues with debounce
  useEffect(() => {
    if (!isOpen || !selectedProjectKey) return;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setIssuesLoading(true);
      setError(null);
      getIntegrationsApi(workspaceId ?? 'default')
        .searchJiraIssues(selectedProjectKey, {
          issueTypes,
          search: searchQuery,
          maxResults: 30,
        })
        .then((results) => {
          setIssues(results);
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'Failed to search issues');
        })
        .finally(() => setIssuesLoading(false));
    }, 350);

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [isOpen, selectedProjectKey, searchQuery, issueTypes, workspaceId]);

  const handleOpen = () => {
    setIsOpen(true);
    setSelectedIssue(null);
    setSearchQuery('');
    setError(null);
    setSaveSuccess(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedIssue(null);
    setSearchQuery('');
    setError(null);
  };

  const handleSave = async () => {
    if (!selectedIssue || !workspaceId) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await registerExternalId({
        workspaceId,
        entityType: 'item',
        internalId: itemId,
        connectorType: 'jira',
        externalId: selectedIssue.key,
        externalUrl: selectedIssue.url,
        externalProjectKey: selectedProjectKey,
      });
      if (saved.error) throw new Error(`The link was not saved: ${saved.error}`);
      // Bring the issue's mapped fields over now (story points into Effort by default).
      const synced = await syncLinkedJiraIssue({
        workspaceId,
        itemId,
        issueKey: selectedIssue.key,
        issueUrl: selectedIssue.url,
        userId: useUserStore.getState().userId,
        reason: 'link',
      });
      if (!synced.ok && synced.errors.length > 0) setError(describeJiraSync(synced));
      setSaveSuccess(true);
      await loadLinks();
      setTimeout(() => {
        setSaveSuccess(false);
        handleClose();
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save link');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (mappingId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    await supabase
      .from('entity_external_ids')
      .update({ is_active: false })
      .eq('id', mappingId);
    await loadLinks();
  };

  const toggleIssueType = (type: string) => {
    setIssueTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="pt-3 border-t border-[var(--rule)]">
      {/* Section header */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide flex items-center gap-1.5">
          <JiraIcon className="w-3.5 h-3.5 text-[#0052CC]" />
          Jira
        </p>
        <div className="flex items-center gap-2">
          {links.length > 0 && !statusLoading && (
            <button
              onClick={() => refreshStatuses(links)}
              className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] transition-colors"
              title="Refresh status from Jira"
            >
              <RefreshCw className="h-3 w-3" />
            </button>
          )}
          {statusLoading && <Loader2 className="h-3 w-3 text-[var(--text-ter)] animate-spin" />}
          {!linksLoading && (
            <button
              onClick={handleOpen}
              className="flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors"
            >
              <Link2 className="h-3 w-3" />
              {links.length > 0 ? 'Add another' : 'Link to Jira'}
            </button>
          )}
        </div>
      </div>

      {/* Existing links */}
      {linksLoading ? (
        <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] py-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading…
        </div>
      ) : links.length === 0 ? (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] italic py-0.5">
          Not linked to Jira
        </p>
      ) : (
        <div className="space-y-1.5">
          {links.map((link) => {
            const status = statusMap[link.externalId];
            return (
              <div
                key={link.externalId}
                className="border border-[var(--rule)] overflow-hidden"
              >
                {/* Issue row */}
                <div className="flex items-center gap-2 px-2 py-1.5 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/40 group">
                  <JiraIcon className="w-3.5 h-3.5 text-[#0052CC] flex-shrink-0" />
                  <span className="text-xs font-mono font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                    {link.externalId}
                  </span>

                  {/* Live status badge */}
                  {status && (
                    <div className="ml-1">
                      <StatusBadge status={status} />
                    </div>
                  )}

                  <div className="ml-auto flex items-center gap-1">
                    {link.externalUrl && (
                      <a
                        href={link.externalUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors"
                        title="Open in Jira"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    {link.mappingId && (
                      <button
                        onClick={() => handleUnlink(link.mappingId!)}
                        className="text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors opacity-0 group-hover:opacity-100"
                        title="Remove link"
                      >
                        <Link2Off className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Close the Loop ──────────────────────────────────────────────── */}
      {links.length > 0 && !loopClosed && (
        <div className="mt-2">
          {/* Auto-prompt when Jira says done */}
          {anyDoneInJira && !confirmingClose && (
            <div className="flex items-start gap-2 p-2 bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] dark:border-[var(--green)]">
              <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] font-medium">
                  Done in Jira
                </p>
                <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5">
                  The linked issue is complete. Archive this item to close the loop.
                </p>
              </div>
              <button
                onClick={() => setConfirmingClose(true)}
                className="flex-shrink-0 px-2.5 py-1 text-xs font-medium bg-[var(--green)] hover:bg-[var(--green)] text-white transition-colors"
              >
                Archive
              </button>
            </div>
          )}

          {/* Confirmation prompt */}
          {confirmingClose && (
            <div className="flex items-center gap-2 p-2 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50 border border-[var(--rule)]">
              <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] flex-1">
                Archive this item and close the loop?
              </p>
              <button
                onClick={() => setConfirmingClose(false)}
                className="px-2 py-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCloseLoop}
                disabled={closingLoop}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-[var(--bg-raised)] hover:bg-[var(--bg)] dark:hover:bg-[var(--surface-raised)] text-white disabled:opacity-50 transition-colors"
              >
                {closingLoop ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Archiving…
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          )}

          {/* Manual trigger — always shown when not in done state and no auto-prompt */}
          {!anyDoneInJira && !confirmingClose && (
            <button
              onClick={() => setConfirmingClose(true)}
              className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] transition-colors pt-0.5"
            >
              <CheckCircle2 className="h-3 w-3" />
              Mark as complete and archive
            </button>
          )}

          {/* Error */}
          {error && (
            <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)] mt-1 flex items-center gap-1">
              <AlertCircle className="h-3 w-3 flex-shrink-0" />
              {error}
            </p>
          )}
        </div>
      )}

      {/* Already archived */}
      {loopClosed && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Loop closed — item archived
        </div>
      )}

      {/* Inline link panel */}
      {isOpen && (
        <div className="mt-3 border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-sm overflow-hidden">
          {/* Panel header */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)]/50">
            <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] flex items-center gap-1.5">
              <Link2 className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              Link to Jira Issue
            </span>
            <button
              onClick={handleClose}
              className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="p-3 space-y-3">
            {/* Not connected state */}
            {jiraConnected === false && (
              <div className="flex items-start gap-2 p-2.5 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)]">
                <AlertCircle className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                  Jira is not connected. Go to{' '}
                  <a
                    href="/prioritization/settings/connectors"
                    className="underline font-medium"
                  >
                    Settings → Connectors
                  </a>{' '}
                  to connect your Jira account.
                </p>
              </div>
            )}

            {/* Project selector */}
            {jiraConnected !== false && (
              <>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                    Project
                  </label>
                  {projectsLoading ? (
                    <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] py-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading projects…
                    </div>
                  ) : (
                    <div className="relative">
                      <select
                        value={selectedProjectKey}
                        onChange={(e) => {
                          setSelectedProjectKey(e.target.value);
                          setSelectedIssue(null);
                          setIssues([]);
                        }}
                        className="w-full appearance-none pl-3 pr-8 py-2 text-xs border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100"
                      >
                        {projects.length === 0 && (
                          <option value="">No projects available</option>
                        )}
                        {projects.map((p) => (
                          <option key={p.key} value={p.key}>
                            {p.name} ({p.key})
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)] pointer-events-none" />
                    </div>
                  )}
                </div>

                {/* Issue type filter */}
                <div>
                  <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                    Issue type
                  </label>
                  <div className="flex gap-1.5">
                    {['Epic', 'Story'].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => toggleIssueType(type)}
                        className={`px-2.5 py-1 text-xs  border font-medium transition-colors ${
                          issueTypes.includes(type)
                            ? type === 'Epic'
                              ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] dark:border-[var(--slate)] dark:bg-[var(--slate)]/30'
                              : 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] dark:border-[var(--slate)] dark:bg-[var(--slate)]/30'
                            : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-ter)] dark:bg-transparent'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Search */}
                <div>
                  <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                    Search issues
                  </label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={itemTitle ? `Search by name (e.g. "${itemTitle.slice(0, 20)}")` : 'Search by name…'}
                      className="w-full pl-8 pr-3 py-2 text-xs border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100"
                    />
                    {issuesLoading && (
                      <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)] animate-spin" />
                    )}
                  </div>
                </div>

                {/* Issue list */}
                {error && (
                  <div className="flex items-start gap-1.5 text-xs text-[var(--coral)] dark:text-[var(--coral)] p-2 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    {error}
                  </div>
                )}

                {!issuesLoading && issues.length === 0 && selectedProjectKey && !error && (
                  <p className="text-xs text-[var(--text-ter)] italic text-center py-2">
                    {searchQuery ? 'No issues match your search' : 'No epics or stories found in this project'}
                  </p>
                )}

                {issues.length > 0 && (
                  <div className="border border-[var(--rule)] overflow-hidden max-h-52 overflow-y-auto">
                    {issues.map((issue) => {
                      const isSelected = selectedIssue?.key === issue.key;
                      return (
                        <button
                          key={issue.key}
                          type="button"
                          onClick={() => setSelectedIssue(isSelected ? null : issue)}
                          className={`w-full flex items-start gap-2 px-3 py-2 text-left text-xs border-b border-[var(--rule)] last:border-0 transition-colors ${
                            isSelected
                              ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20'
                              : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/30'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="font-mono font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] flex-shrink-0">
                                {issue.key}
                              </span>
                              <IssueTypeBadge type={issue.issueType} />
                            </div>
                            <p className="text-[var(--text-sec)] dark:text-[var(--text-sec)] truncate leading-tight">
                              {issue.summary}
                            </p>
                            {issue.status && (
                              <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                                {issue.status}
                              </p>
                            )}
                          </div>
                          {isSelected && (
                            <Check className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Save button */}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-3 py-1.5 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!selectedIssue || saving || saveSuccess}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[var(--slate)] hover:bg-[var(--slate)] text-white"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Saving…
                      </>
                    ) : saveSuccess ? (
                      <>
                        <Check className="h-3 w-3" />
                        Linked!
                      </>
                    ) : (
                      <>
                        <Link2 className="h-3 w-3" />
                        Link {selectedIssue ? selectedIssue.key : 'issue'}
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
